import { Node } from '@tiptap/core';
import { type Slice, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import {
  MENTION_TAG_NAME,
  MENTION_TEXT,
  VARIABLE_TAG_NAME,
  EDITOR_DATA_ATTR,
  CSS_CLASSES,
  INPUT_TAG_PATTERN,
  EMPTY_VALUE_TEXT,
} from './constants';
import { getTagDisplay } from './utils';
import type { AvailableInput } from './types';
import type { Editor } from '@tiptap/react';
import type { PromptDisplayMode } from 'web';

// JSON content type for Tiptap
interface TiptapJSONContent {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapJSONContent[];
  text?: string;
}

interface InsertTagParamsCommon {
  editor: Editor;
  tagKey: string;
  label?: string;
  value?: string;
  isIterator?: boolean;
  showLabel?: boolean;
  showValue?: boolean;
}

interface InsertTagParams extends InsertTagParamsCommon {
  insertAtEnd?: boolean;
  scrollContainer?: HTMLElement | null;
}

interface DuplicateTagAtPositionParams {
  editor: Editor;
  tagKey: string;
  tagPos: number;
  availableInputs: AvailableInput[];
  displayMode: PromptDisplayMode;
}

/**
 * Parse a line of text with tag markers and build paragraph content.
 * The pattern determines which format is matched (e.g. {{key}} or {{key:value}}).
 */
function buildParagraphContent(
  line: string,
  keyToInfo: Map<string, AvailableInput>,
  displayMode: PromptDisplayMode,
): TiptapJSONContent[] {
  const content: TiptapJSONContent[] = [];
  let lastIndex = 0;

  // Reset regex state
  INPUT_TAG_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = INPUT_TAG_PATTERN.exec(line)) !== null) {
    const tagKey = match[1];
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;

    // Add text before the tag
    if (matchStart > lastIndex) {
      content.push({ type: 'text', text: line.slice(lastIndex, matchStart) });
    }

    // Add the tag node if the key exists in availableInputs
    const info = keyToInfo.get(tagKey);
    if (info) {
      content.push({
        type: VARIABLE_TAG_NAME,
        attrs: {
          tagKey,
          ...getTagDisplay(info, displayMode),
        },
      });
    } else {
      // Keep the marker as text if no corresponding input
      content.push({ type: 'text', text: match[0] });
    }

    lastIndex = matchEnd;
  }

  // Add remaining text after the last tag
  if (lastIndex < line.length) {
    content.push({ type: 'text', text: line.slice(lastIndex) });
  }

  return content;
}

/**
 * Parse a string with tag markers and build editor content.
 * Markers matching availableInputs are converted to inline tag nodes.
 * Newlines create separate paragraphs.
 * The optional pattern parameter allows matching different formats
 * (e.g. {{key}} for stored values, or {{key:value}} for pasted text).
 */
export function buildEditorContent(
  value: string,
  availableInputs: AvailableInput[],
  displayMode: PromptDisplayMode,
): TiptapJSONContent[] {
  // Create a map from key to info for quick lookup
  const keyToInfo = new Map(availableInputs.map((info) => [info.key, info]));

  // Split by newlines and create a paragraph for each line
  const lines = value.split('\n');

  return lines.map((line) => {
    const content = buildParagraphContent(line, keyToInfo, displayMode);
    return {
      type: 'paragraph',
      content: content.length > 0 ? content : undefined,
    };
  });
}

/**
 * Serialize a single tag node to its text representation: {{tagKey:tagValue}} or {{tagKey}}.
 * Returns empty string for mention trigger tags.
 */
function serializeTagToText(node: ProseMirrorNode, includeValue: boolean = false): string {
  const tagKey = node.attrs.tagKey as string;
  if (tagKey === MENTION_TAG_NAME) return '';

  const tagValue = (node.attrs.value as string) || '';
  if (includeValue && tagValue && tagValue !== EMPTY_VALUE_TEXT) {
    return `{{${tagKey}:${tagValue.replaceAll('\n', ' ')}}}`;
  }
  return `{{${tagKey}}}`;
}

/**
 * Serialize a node's inline content to text, representing tags as {{tagKey:tagValue}} or {{tagKey}}.
 */
function serializeNodeToTagText(node: ProseMirrorNode, includeValue: boolean = false): string {
  let text = '';
  node.forEach((child) => {
    if (child.isText) {
      text += child.text ?? '';
    } else if (child.type.name === VARIABLE_TAG_NAME) {
      text += serializeTagToText(child, includeValue);
    }
  });
  return text;
}

/**
 * Serialize a ProseMirror Slice to plain text with tags represented as {{tagKey:tagValue}}.
 * This is used as the clipboardTextSerializer editor prop so that copying content
 * preserves tag information in the plain text clipboard format.
 */
export function serializeClipboardText(slice: Slice): string {
  const paragraphs: string[] = [];

  slice.content.forEach((node) => {
    if (node.type.name === 'paragraph') {
      paragraphs.push(serializeNodeToTagText(node, true));
    } else if (node.type.name === VARIABLE_TAG_NAME) {
      // Handle direct inline tag nodes (e.g. from node selection)
      const text = serializeTagToText(node, true);
      if (text) paragraphs.push(text);
    }
  });

  return paragraphs.join('\n');
}

/**
 * Extract text content from editor, serializing tags back to {{key}} format.
 * The mention trigger tag is serialized as "@" instead of "{{@mention}}".
 */
export function extractTextFromEditor(editor: Editor): string {
  const paragraphs: string[] = [];

  editor.state.doc.forEach((node) => {
    if (node.type.name === 'paragraph') {
      paragraphs.push(serializeNodeToTagText(node));
    }
  });

  return paragraphs.join('\n');
}

/**
 * Scan the editor document for text patterns like {{tagKey}} or {{tagKey:value}}
 * and convert them to actual tag nodes when the tagKey matches one of the given inputs.
 * Called when new variables are added so that previously typed placeholder text
 * is automatically converted into real tags.
 */
export function convertTextPatternsToTags(
  editor: Editor,
  addedInputs: AvailableInput[],
  displayMode: PromptDisplayMode,
): void {
  if (addedInputs.length === 0) return;

  const { tr, schema } = editor.state;
  const tagNodeType = schema.nodes[VARIABLE_TAG_NAME];

  if (!tagNodeType) return;

  const addedByKey = new Map(addedInputs.map((input) => [input.key, input]));

  // Collect all replacements first (in reverse document order) to avoid position shifts
  const replacements: { from: number; to: number; input: AvailableInput }[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    // Reset regex state for each text node
    INPUT_TAG_PATTERN.lastIndex = 0;

    let match: RegExpExecArray | null;
    while ((match = INPUT_TAG_PATTERN.exec(node.text)) !== null) {
      const matchedInput = addedByKey.get(match[1]);
      if (matchedInput) {
        const from = pos + match.index;
        const to = from + match[0].length;
        replacements.push({ from, to, input: matchedInput });
      }
    }
  });

  if (replacements.length === 0) return;

  // Apply replacements in reverse order so positions remain valid
  replacements.sort((a, b) => b.from - a.from);

  for (const { from, to, input } of replacements) {
    const tagNode = tagNodeType.create({
      tagKey: input.key,
      ...getTagDisplay(input, displayMode),
    });
    tr.replaceWith(from, to, tagNode);
  }

  editor.view.dispatch(tr);
}

/**
 * Convert tag nodes back to text {{tagKey}} when their corresponding inputs
 * have been removed. Called when variables are deleted so that orphaned tags
 * are replaced with their text representation.
 */
export function convertTagsToText(editor: Editor, removedKeys: Set<string>): void {
  if (removedKeys.size === 0) return;

  const { tr } = editor.state;

  // Collect all tag positions to replace (in reverse order to avoid position shifts)
  const replacements: { from: number; to: number; text: string }[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === VARIABLE_TAG_NAME && node.attrs.tagKey !== MENTION_TAG_NAME) {
      const tagKey = node.attrs.tagKey as string;
      if (removedKeys.has(tagKey)) {
        replacements.push({ from: pos, to: pos + node.nodeSize, text: `{{${tagKey}}}` });
      }
    }
    return true;
  });

  if (replacements.length === 0) return;

  // Apply replacements in reverse order so positions remain valid
  replacements.sort((a, b) => b.from - a.from);

  for (const { from, to, text } of replacements) {
    tr.replaceWith(from, to, editor.state.schema.text(text));
  }

  editor.view.dispatch(tr);
}

/**
 * Find the mention trigger tag in the editor and return its position and queryText.
 * Returns { triggerPos: -1, queryText: '' } if no mention trigger is found.
 */
export function findMentionTrigger(editor: Editor): { triggerPos: number; queryText: string } {
  let triggerPos = -1;
  let queryText = '';
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === VARIABLE_TAG_NAME && node.attrs.tagKey === MENTION_TAG_NAME) {
      triggerPos = pos;
      queryText = (node.attrs.queryText as string) || '';
      return false;
    }
    return true;
  });
  return { triggerPos, queryText };
}

/**
 * Remove the mention trigger "@" tag from the editor.
 * When keepText is true, the "@" + queryText is reinserted as plain text (used when dismissing the menu).
 * When keepText is false, the tag is simply deleted.
 */
export function removeMentionTrigger(editor: Editor, keepText: boolean = false): void {
  const { triggerPos, queryText } = findMentionTrigger(editor);

  if (triggerPos >= 0) {
    const chain = editor
      .chain()
      .focus()
      .deleteRange({ from: triggerPos, to: triggerPos + 1 });

    if (keepText) {
      chain.insertContentAt(triggerPos, `${MENTION_TEXT}${queryText}`);
    }

    chain.run();
  }
}

/**
 * Insert a tag at the current cursor position.
 * This is used when adding a variable programmatically (e.g., via button click).
 */
export function insertTag({ editor, insertAtEnd = false, scrollContainer, ...tagAttributes }: InsertTagParams) {
  if (insertAtEnd) {
    editor.commands.focus('end');
  }

  const position = editor.state.selection.anchor;

  editor
    .chain()
    .insertContentAt(position, {
      type: VARIABLE_TAG_NAME,
      attrs: tagAttributes,
    })
    .run();

  // Defer focus and scrolling to after React re-render completes
  requestAnimationFrame(() => {
    editor
      .chain()
      .focus()
      .setTextSelection(position + 1)
      .run();

    // Manually scroll the container to bottom if inserting at end
    if (insertAtEnd && scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  });
}

/**
 * Duplicate a tag at the specified position.
 * Inserts a copy of the tag right after the original.
 */
export function duplicateTagAtPosition({
  editor,
  tagKey,
  tagPos,
  availableInputs,
  displayMode,
}: DuplicateTagAtPositionParams): void {
  const inputInfo = availableInputs.find((i) => i.key === tagKey);
  if (!inputInfo) return;

  // Insert a duplicate tag right after the original tag
  editor
    .chain()
    .focus()
    .insertContentAt(tagPos + 1, {
      type: VARIABLE_TAG_NAME,
      attrs: {
        tagKey,
        ...getTagDisplay(inputInfo, displayMode),
      },
    })
    .run();
}

/**
 * Insert a tag at the mention trigger position, replacing the "@" trigger.
 * This is used when selecting a variable from the mention menu or creating a new one.
 */
export function insertTagAtMention({ editor, ...tagAttributes }: InsertTagParamsCommon) {
  // Find the mention trigger tag position
  const { triggerPos } = findMentionTrigger(editor);

  if (triggerPos >= 0) {
    // Replace the mention trigger with the tag at its position
    editor
      .chain()
      .focus()
      .deleteRange({ from: triggerPos, to: triggerPos + 1 })
      .insertContentAt(triggerPos, {
        type: VARIABLE_TAG_NAME,
        attrs: tagAttributes,
      })
      .run();

    // Defer focus to after React re-render completes
    requestAnimationFrame(() => {
      editor
        .chain()
        .focus()
        .setTextSelection(triggerPos + 1)
        .run();
    });
  }
}

export const CreateTagNode = () => {
  return Node.create({
    name: VARIABLE_TAG_NAME,
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
      return {
        tagKey: {
          default: '',
        },
        label: {
          default: '',
        },
        value: {
          default: '',
        },
        queryText: {
          default: '',
        },
        isIterator: {
          default: false,
        },
        showLabel: {
          default: true,
        },
        showValue: {
          default: true,
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: `span[${EDITOR_DATA_ATTR.TAG_KEY}]`,
          getAttrs: (element) => {
            if (typeof element === 'string') return false;
            return {
              tagKey: element.getAttribute(EDITOR_DATA_ATTR.TAG_KEY) || '',
              label: element.getAttribute(EDITOR_DATA_ATTR.TAG_LABEL) || '',
              value: element.getAttribute(EDITOR_DATA_ATTR.TAG_VALUE) || '',
              isIterator: element.getAttribute(EDITOR_DATA_ATTR.TAG_IS_ITERATOR) === 'true',
              showLabel: element.getAttribute(EDITOR_DATA_ATTR.TAG_SHOW_LABEL) !== 'false',
              showValue: element.getAttribute(EDITOR_DATA_ATTR.TAG_SHOW_VALUE) !== 'false',
            };
          },
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      const tagKey = HTMLAttributes.tagKey as string;
      const label = HTMLAttributes.label as string;
      const value = HTMLAttributes.value as string;
      const isIterator = HTMLAttributes.isIterator as boolean;
      const showLabel = HTMLAttributes.showLabel as boolean;
      const showValue = HTMLAttributes.showValue as boolean;

      // Special case: mention trigger tag should show "@" + queryText
      if (tagKey === MENTION_TAG_NAME) {
        const mentionQueryText = (HTMLAttributes.queryText as string) || '';
        return [
          'span',
          {
            [EDITOR_DATA_ATTR.TAG_KEY]: tagKey,
            contenteditable: 'false',
          },
          `${MENTION_TEXT}${mentionQueryText}`,
        ];
      }

      // Common data attributes
      const dataAttrs = {
        [EDITOR_DATA_ATTR.TAG_KEY]: tagKey,
        [EDITOR_DATA_ATTR.TAG_LABEL]: label || '',
        [EDITOR_DATA_ATTR.TAG_VALUE]: value || '',
        [EDITOR_DATA_ATTR.TAG_IS_ITERATOR]: String(isIterator),
        [EDITOR_DATA_ATTR.TAG_SHOW_LABEL]: String(showLabel),
        [EDITOR_DATA_ATTR.TAG_SHOW_VALUE]: String(showValue),
        contenteditable: 'false',
        draggable: 'true',
      };

      if (!label && !value) {
        // Fallback if input info is not available
        return ['span', dataAttrs, EMPTY_VALUE_TEXT];
      }

      // Type for Tiptap renderHTML structure (tag, attributes, children)
      type HTMLElement = [string, Record<string, string>, ...(string | HTMLElement)[]];
      type HTMLChild = string | HTMLElement;
      const content: HTMLChild[] = [];

      // If this is an iterator, add an icon before the label
      if (isIterator) {
        // Add iterator icon using CSS mask-image (avoids SVG namespace issues in TipTap renderHTML)
        // The SVG is encoded as a data URI and used as a mask, with background-color for the color
        const svgDataUri = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18.75 7.05005L21 4.80005L18.75 2.55005" stroke="black" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 10.8C3 9.20875 3.63214 7.68263 4.75736 6.55741C5.88258 5.43219 7.4087 4.80005 9 4.80005H21" stroke="black" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.25 16.95L3 19.2L5.25 21.45" stroke="black" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 13.2C21 14.7913 20.3679 16.3174 19.2426 17.4426C18.1174 18.5678 16.5913 19.2 15 19.2H3" stroke="black" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.4 10.9V10H9V10.9" stroke="black" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/><path d="M10.7998 14.5H12.5998" stroke="black" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.7002 10V14.5" stroke="black" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;
        content.push([
          'span',
          {
            class: 'tag-iterator-icon',
            style: `display: inline-block; width: 16px; height: 16px; vertical-align: middle; margin-bottom: 2px; margin-right: 4px; background-color: currentColor; -webkit-mask-image: url("${svgDataUri}"); -webkit-mask-size: contain; -webkit-mask-repeat: no-repeat; mask-image: url("${svgDataUri}"); mask-size: contain; mask-repeat: no-repeat;`,
          },
        ]);
      }

      if (label && showLabel) {
        content.push(['span', { class: CSS_CLASSES.TAG_LABEL }, label]);
      }

      if (value && showValue) {
        const isEmptyTag = !value.trim() || value === EMPTY_VALUE_TEXT;
        const valueClass = isEmptyTag
          ? `${CSS_CLASSES.TAG_VALUE} ${CSS_CLASSES.TAG_VALUE_EMPTY}`
          : CSS_CLASSES.TAG_VALUE;

        if (!value.includes('\n')) {
          content.push(['span', { class: valueClass }, value]);
        } else {
          const lines = value.split('\n');
          const valueContent: HTMLChild[] = [];
          lines.forEach((line, i) => {
            if (i > 0) valueContent.push(['br', {}]);
            if (line) valueContent.push(line);
          });
          content.push(['span', { class: valueClass }, ...valueContent]);
        }
      }

      return ['span', dataAttrs, ...content];
    },
  });
};
